import cart from '@/store/modules/cart';
import user from '@/store/modules/user';
import checkout from '@/store/modules/checkout';
import payment from '@/store/modules/payment';
import cms from '@/store/modules/cms';
import storelocator from '@/store/modules/storelocator';
import store from '@/store';
import { bus } from './bus';
import { mockData, setJWT } from '@/store/api';
import fidelitycard from '@/store/modules/fidelitycard';
import { ShippingInfoData } from '@/store/models';
import { decode as decodeShipment } from '@/commons/shipmentMethodHelpers';
import { get as dataGetter, rest } from 'lodash';
import { areEqualAddresses, copyAddressData, normalizeQuoteAddress } from '@/commons/addressNormalizer';
import { flagNotificationEnabled } from '@/commons/toast';

export const event = 'app.init';

function getCartInfos(resolve: any, data: any) {
    const promises: any = [
        cart.getCart(),
        cart.getTotals(),
    ];

    let userEmail = '';
    if (process.env.VUE_APP_FLOW === 'logged' || process.env.VUE_APP_FLOW === 'loggedsess' && data.user) {
        userEmail = data.user;
    }

    Promise.all(promises).then(function(values) {
        const innerpromises: any = [
            checkout.estimateShipping(checkout.defaultShippingAddress),
            checkout.getCoupon(),
            checkout.getPaymentMethods(),
            checkout.getSelectedPayment(),
            mockData(userEmail),
        ];

        Promise.all(innerpromises).then(function(values2) {
            assignDefaultUserNameToAddress();
            assignDefaultShippingMethod();
            assignDefaultBillingAddress();
            handleLastOrderData({lastOrderData: null});
            resolve([values, values2]);

            const res: any = values2[4];
            // console.log(res);
            storelocator.setStores(res.storelocators);
            store.state.countryData = res.countrydata;
            store.state.options = res.settings;
            // store.state.options = res.generalOptions;
            cms.setAgreements(res.agreements);
            cms.setBlocks(res.cmsblocks);
            cms.setCartbanners(res.cartbanners);
            if (res.checkoutEmail) {
                checkout.setEmail(res.checkoutEmail);
            }
            if (res.selectedStoreLocatorId) {
                storelocator.setSelectedStoreId(res.selectedStoreLocatorId);
            }
        });
    });
}

function assignDefaultUserNameToAddress() {
    if (user.isLoggedin) {
        if (!user.hasAddresses) {
            checkout.shippingAddress.firstname = user.customer.firstname;
            checkout.shippingAddress.lastname = user.customer.lastname;
            checkout.billingAddress.firstname = user.customer.firstname;
            checkout.billingAddress.lastname = user.customer.lastname;
        }
        if (!checkout.email) {
            checkout.setEmail(user.customer.email);
        }
    }
}

function assignDefaultShippingMethod() {
    if (!user.isLoggedin
        && !cart.isEmpty
        && checkout.selectedShipment.toString() === ''
    ) {
        if (store.state.options.carrier.default) {
            const defaultMethod = store.state.options.carrier.default;
            const data: ShippingInfoData = {
                address: checkout.defaultShippingAddress,
                shippingmethod: decodeShipment(defaultMethod),
            };
            checkout.setSelectedShipment(defaultMethod);
            checkout.setShippingInfo(data);
        }
    }
}

function assignDefaultBillingAddress() {
    if (user.isLoggedin && user.hasAddresses) {
        if (user.billingAddress && !checkout.billingAddress.firstname) {
          const billingAddress = normalizeQuoteAddress(checkout.billingAddress, true);
          copyAddressData(user.billingAddress, billingAddress);
          checkout.assignBillingAddress(normalizeQuoteAddress(billingAddress));
        }
    }
}

function handleLastOrderData(initialState: any) {
    // handle last order shipping/payment - https://support.webformat.com/issues/21299
    if (initialState.lastOrderData) {
        flagNotificationEnabled(false);

        const promises = [];
        const lastPaymentMethod = initialState.lastOrderData.paymentMethod;
        if (lastPaymentMethod && checkout.hasPaymentMethod(lastPaymentMethod)) {
          promises.push(checkout.setPayment(lastPaymentMethod));
        }

        const lastShipmentMethod = initialState.lastOrderData.shippingMethod;
        if (lastShipmentMethod && checkout.hasShippingMethod(lastShipmentMethod)) {
          const data: any = {
            address: checkout.defaultShippingAddress,
            shippingmethod: decodeShipment(lastShipmentMethod),
          };
          checkout.setSelectedShipment(lastShipmentMethod);
          promises.push(checkout.setShippingInfo(data));

          if (initialState.lastOrderData.storePickupId) {
            promises.push(storelocator.saveSelectedStore(initialState.lastOrderData.storePickupId));
          }
        }

        if (promises.length) {
            Promise.all(promises).finally(() => {
                checkout.initializeStepStatus();
                flagNotificationEnabled(true);
            });
        } else {
            flagNotificationEnabled(true);
        }
    }
}

bus.$on(event, (data: any) => {
    // [+] register payment

    // cash on delivery
    if (checkout.hasPaymentMethod('phoenix_cashondelivery')) {
        payment.registerMethod({ methodCode: 'phoenix_cashondelivery' });
    }

    // Klarna
    const klarnaConfigs = dataGetter(store.state.options.payment, 'klarnaConfig.payment');
    if (klarnaConfigs && klarnaConfigs.klarna_kp.enabled) {
        payment.registerMethod({ methodCode: 'klarna', params: klarnaConfigs });
    }

    // [-] register payment

    if (process.env.NODE_ENV !== 'production') {
        if (data.cart) {
            cart.setToken(data.cart);
            getCartInfos(data.resolveProm, {});
        } else if (data.user) {
            user.login({username: data.user, password: data.pass, cartToken: ''}).then(() => {
                getCartInfos(data.resolveProm, {user: data.user});
                fidelitycard.getFidelityCard();
                checkout.setEmail(data.user);
            });
        } else if (data.token) {
            setJWT(data.token);
            user.getMe().then((res: any) => {
                fidelitycard.getFidelityCard();
                getCartInfos(data.resolveProm, {user: user.customer.email});
                checkout.setEmail(user.customer.email);
            }).catch((e) => {
                console.log(e);
                localStorage.removeItem('usertoken');
            });
        }
    } else {
        assignDefaultUserNameToAddress();
        assignDefaultShippingMethod();
        assignDefaultBillingAddress();
        handleLastOrderData(data.initialState);
    }
});
